import React from 'react';
import { Link } from 'react-router-dom';
import { Zap, Clock, DollarSign, Shield, Smartphone, QrCode, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import FAQStructuredData from '../components/FAQStructuredData';

const PIX: React.FC = () => {
  useSEO({
    title: 'Guia Completo do PIX 2026: PIX Automático, por Aproximação, Garantido e Regras | O Teu Dinheiro',
    description: 'Tudo sobre o PIX em 2026: como funciona o PIX Automático, PIX por aproximação, PIX Garantido parcelado, limites de segurança e como evitar golpes.',
    keywords: 'PIX 2026, PIX automático, PIX por aproximação, PIX garantido, novas regras do PIX, segurança no PIX, como funciona o PIX',
    url: 'https://www.oteudinheiro.com.br/pix'
  });

  const pixFaq = [
    {
      question: 'O PIX continua gratuito para pessoas físicas em 2026?',
      answer: 'Sim! Transferências e pagamentos via PIX, inclusive nas modalidades PIX por Aproximação e PIX Automático, continuam 100% gratuitos para pessoas físicas.'
    },
    {
      question: 'Como funciona o PIX Automático para assinaturas e contas fixas?',
      answer: 'O PIX Automático permite autorizar cobranças recorrentes (como luz, internet, academia e streaming) direto pelo aplicativo do banco, substituindo o débito automático tradicional e o cartão de crédito.'
    },
    {
      question: 'O que fazer se cair em um golpe do PIX?',
      answer: 'Acione imediatamente o banco pelo aplicativo ou canal oficial solicitando a abertura do MED (Mecanismo Especial de Devolução) e registre um Boletim de Ocorrência.'
    }
  ];
  const pixFeatures = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Transferência Instantânea',
      description: 'Dinheiro disponível em segundos, 24 horas por dia'
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Funciona 24/7',
      description: 'Não importa o horário, PIX sempre funciona'
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Gratuito',
      description: 'Sem taxas para pessoas físicas'
    }
  ];

  const howToUse = [
    {
      step: '1',
      title: 'Chave PIX',
      description: 'Configure sua chave no app do banco (CPF, email, celular)',
      icon: <Smartphone className="w-6 h-6" />
    },
    {
      step: '2',
      title: 'Transferir',
      description: 'Use a chave da pessoa para enviar dinheiro',
      icon: <Zap className="w-6 h-6" />
    },
    {
      step: '3',
      title: 'Receber',
      description: 'Compartilhe sua chave para receber pagamentos',
      icon: <CheckCircle className="w-6 h-6" />
    },
    {
      step: '4',
      title: 'QR Code',
      description: 'Escaneie para pagamentos rápidos',
      icon: <QrCode className="w-6 h-6" />
    }
  ];

  const securityTips = [
    'Nunca compartilhe sua senha ou código de confirmação',
    'Bancos nunca pedem essas informações por mensagem',
    'Sempre verifique o nome do destinatário antes de confirmar',
    'Use apenas apps oficiais dos bancos',
    'Ative a verificação em duas etapas quando disponível'
  ];

  return (
    <div className="min-h-screen">
      <FAQStructuredData items={pixFaq} />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 section-padding">
        <div className="container-custom text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Entendendo o PIX
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            O sistema de pagamento instantâneo que revolucionou as transações no Brasil. 
            Rápido, seguro e gratuito para pessoas físicas.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Por que o PIX é revolucionário?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              O PIX transformou a forma como os brasileiros fazem transações, 
              oferecendo uma experiência mais rápida e eficiente.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pixFeatures.map((feature, index) => (
              <div key={index} className="card p-8 text-center group hover:-translate-y-2 transition-all duration-300">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 transition-colors">
                  <div className="text-blue-600">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Use Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - How to Use */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Como Usar o PIX
                </h2>
                <div className="space-y-6">
                  {howToUse.map((item, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <div className="text-blue-600">
                          {item.icon}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {item.step}
                          </span>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {item.title}
                          </h3>
                        </div>
                        <p className="text-gray-600">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Visual Guide */}
            <div className="card p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Smartphone className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Guia Visual
                </h3>
                <p className="text-gray-600">
                  Passo a passo para configurar e usar o PIX
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2">📱 No App do Banco:</h4>
                  <ul className="text-blue-800 text-sm space-y-1">
                    <li>• Acesse "PIX" ou "Pagamentos"</li>
                    <li>• Escolha "Minhas Chaves"</li>
                    <li>• Adicione CPF, email ou celular</li>
                    <li>• Confirme com sua senha</li>
                  </ul>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">💸 Para Enviar:</h4>
                  <ul className="text-green-800 text-sm space-y-1">
                    <li>• Digite a chave PIX do destinatário</li>
                    <li>• Confirme o nome da pessoa</li>
                    <li>• Digite o valor</li>
                    <li>• Confirme a transação</li>
                  </ul>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-purple-900 mb-2">📋 Para Receber:</h4>
                  <ul className="text-purple-800 text-sm space-y-1">
                    <li>• Compartilhe sua chave PIX</li>
                    <li>• Ou gere um QR Code</li>
                    <li>• Dinheiro cai na hora</li>
                    <li>• Sem taxas ou espera</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left Column - Security Tips */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Segurança é Fundamental
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  O PIX é seguro, mas é importante seguir algumas práticas 
                  para proteger seu dinheiro e dados.
                </p>
                
                <div className="space-y-4">
                  {securityTips.map((tip, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Warning Box */}
            <div className="warning-box">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-8 h-8 text-accent-600 mt-1" />
                <div>
                  <h4 className="text-xl font-semibold text-gray-900 mb-3">
                    ⚠️ Cuidado com Golpes
                  </h4>
                  <p className="text-gray-700 mb-4">
                    Criminosos podem tentar enganar você com mensagens falsas. 
                    Lembre-se: bancos NUNCA pedem senhas ou códigos por mensagem.
                  </p>
                  <div className="bg-accent-100 p-4 rounded-lg">
                    <h5 className="font-semibold text-accent-900 mb-2">🚨 Sinais de Golpe:</h5>
                    <ul className="text-accent-800 text-sm space-y-1">
                      <li>• Mensagens urgentes pedindo ação imediata</li>
                      <li>• Links suspeitos ou não oficiais</li>
                      <li>• Pedidos de senha ou código de confirmação</li>
                      <li>• Promessas de dinheiro fácil ou prêmios</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Vantagens do PIX
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              O PIX trouxe benefícios significativos para todos os brasileiros, 
              democratizando o acesso a pagamentos digitais.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Zap className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Velocidade
              </h3>
              <p className="text-gray-600 text-sm">
                Transações instantâneas, sem esperar dias para o dinheiro cair
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Economia
              </h3>
              <p className="text-gray-600 text-sm">
                Sem taxas para pessoas físicas, economizando dinheiro
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Disponibilidade
              </h3>
              <p className="text-gray-600 text-sm">
                Funciona 24 horas por dia, 7 dias por semana
              </p>
            </div>

            <div className="card p-6 text-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Segurança
              </h3>
              <p className="text-gray-600 text-sm">
                Sistema seguro do Banco Central do Brasil
              </p>
            </div>
          </div>

          {/* Novidades do PIX em 2026 (Internal Links Cluster) */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
              Novidades do PIX em 2026
            </h2>
            <p className="text-center text-gray-600 max-w-2xl mx-auto mb-10">
              Confira nossos guias completos sobre os novos recursos lançados pelo Banco Central:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link
                to="/blog/novas-regras-pix-2026"
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all"
              >
                <span className="text-xs font-semibold uppercase text-blue-600">Regulamentação</span>
                <h3 className="text-base font-bold text-gray-900 mt-1 mb-2">
                  Novas Regras do PIX em 2026: O que muda?
                </h3>
                <span className="inline-flex items-center text-sm font-semibold text-blue-600">
                  Ler artigo <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/pix-aproximacao-automatico-2026"
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all"
              >
                <span className="text-xs font-semibold uppercase text-blue-600">NFC e Carteiras</span>
                <h3 className="text-base font-bold text-gray-900 mt-1 mb-2">
                  PIX por Aproximação e Automático: Como ativar
                </h3>
                <span className="inline-flex items-center text-sm font-semibold text-blue-600">
                  Ler artigo <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/pix-garantido-2026"
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all"
              >
                <span className="text-xs font-semibold uppercase text-blue-600">Parcelamento</span>
                <h3 className="text-base font-bold text-gray-900 mt-1 mb-2">
                  PIX Garantido: Como parcelar compras sem cartão
                </h3>
                <span className="inline-flex items-center text-sm font-semibold text-blue-600">
                  Ler artigo <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>

              <Link
                to="/blog/pix-internacional-2026"
                className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:border-blue-500 hover:shadow-md transition-all"
              >
                <span className="text-xs font-semibold uppercase text-blue-600">Exterior</span>
                <h3 className="text-base font-bold text-gray-900 mt-1 mb-2">
                  PIX Internacional: Onde já é aceito em 2026
                </h3>
                <span className="inline-flex items-center text-sm font-semibold text-blue-600">
                  Ler artigo <ArrowRight className="w-4 h-4 ml-1" />
                </span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PIX;
